import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, AlertTriangle, Upload, FileText, Image, Trash2, CheckCircle, Clock, RefreshCw, ChevronDown, ChevronUp, Shield, MessageSquare, Paperclip } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })?.format(new Date(d));
};

const formatDateShort = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })?.format(new Date(d));
};

const formatAmount = (amount, currency = 'XOF') => {
  if (!amount) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(amount);
};

// ─── Dispute Status Config ────────────────────────────────────────────────────

const DISPUTE_STATUS_CONFIG = {
  OPEN:              { label: 'Ouvert',              color: 'text-red-600',    bg: 'bg-red-50',    border: 'border-red-200',    icon: AlertTriangle, step: 1 },
  open:              { label: 'Ouvert',              color: 'text-red-600',    bg: 'bg-red-50',    border: 'border-red-200',    icon: AlertTriangle, step: 1 },
  UNDER_REVIEW:      { label: 'En cours d\'examen',  color: 'text-amber-600',  bg: 'bg-amber-50',  border: 'border-amber-200',  icon: Clock,         step: 2 },
  RESOLVED_BUYER:    { label: 'Résolu (acheteur)',   color: 'text-green-600',  bg: 'bg-green-50',  border: 'border-green-200',  icon: CheckCircle,   step: 3 },
  RESOLVED_SELLER:   { label: 'Résolu (vendeur)',    color: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-200',   icon: CheckCircle,   step: 3 },
  resolved:          { label: 'Résolu',              color: 'text-green-600',  bg: 'bg-green-50',  border: 'border-green-200',  icon: CheckCircle,   step: 3 },
  DISMISSED:         { label: 'Classé sans suite',   color: 'text-gray-500',   bg: 'bg-gray-50',   border: 'border-gray-200',   icon: X,             step: 3 },
};

const DISPUTE_TIMELINE_STEPS = [
  { key: 'submitted',    label: 'Soumis',       icon: FileText },
  { key: 'under_review', label: 'En examen',    icon: Clock },
  { key: 'resolved',     label: 'Résolu',       icon: CheckCircle },
];

const DISPUTE_CATEGORIES = [
  { value: 'non_delivery',      label: 'Non-livraison du bien' },
  { value: 'quality_issue',     label: 'Problème de qualité / état du bien' },
  { value: 'price_dispute',     label: 'Désaccord sur le prix' },
  { value: 'contract_breach',   label: 'Non-respect du contrat' },
  { value: 'fraud',             label: 'Suspicion de fraude' },
  { value: 'documentation',     label: 'Documents manquants ou incorrects' },
  { value: 'other',             label: 'Autre motif' },
];

// ─── Resolution Timeline ──────────────────────────────────────────────────────

const ResolutionTimeline = ({ dispute }) => {
  const status = dispute?.status || 'OPEN';
  const cfg = DISPUTE_STATUS_CONFIG?.[status] || DISPUTE_STATUS_CONFIG?.OPEN;
  const currentStep = cfg?.step ?? 1;

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Chronologie de résolution</h4>
      <div className="flex items-start gap-2">
        {DISPUTE_TIMELINE_STEPS?.map((step, idx) => {
          const stepNum = idx + 1;
          const isCompleted = currentStep > stepNum;
          const isCurrent = currentStep === stepNum;
          const StepIcon = step?.icon;
          return (
            <React.Fragment key={step?.key}>
              <div className="flex flex-col items-center gap-1 flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  isCompleted ? 'bg-green-500 text-white' : isCurrent ?'bg-red-500 text-white': 'bg-gray-100 text-gray-400'
                }`}>
                  <StepIcon className="w-3.5 h-3.5" />
                </div>
                <span className={`text-[10px] font-semibold text-center whitespace-nowrap ${
                  isCurrent ? 'text-red-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                }`}>{step?.label}</span>
              </div>
              {idx < DISPUTE_TIMELINE_STEPS?.length - 1 && (
                <div className={`flex-1 h-0.5 mt-4 rounded-full transition-colors ${
                  currentStep > stepNum + 1 ? 'bg-green-400' : currentStep > stepNum ? 'bg-amber-300' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Status badge + dates */}
      <div className="flex items-center justify-between pt-1">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg?.bg} ${cfg?.color} ${cfg?.border}`}>
          {cfg?.icon && <cfg.icon className="w-3 h-3" />}
          {cfg?.label}
        </span>
        <div className="text-right">
          <p className="text-[10px] text-gray-400">Soumis le</p>
          <p className="text-xs font-semibold text-gray-700">{formatDateShort(dispute?.created_at)}</p>
        </div>
      </div>

      {/* Resolution note if available */}
      {(dispute?.resolution_notes || dispute?.resolution_note) && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
          <p className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Note de résolution
          </p>
          <p className="text-xs text-green-800">{dispute?.resolution_notes || dispute?.resolution_note}</p>
        </div>
      )}
    </div>
  );
};

// ─── Evidence File Item ───────────────────────────────────────────────────────

const EvidenceFileItem = ({ file, onRemove, isUploaded = false }) => {
  const isImage = file?.type?.startsWith('image/') || file?.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isPdf = file?.type === 'application/pdf' || file?.name?.endsWith('.pdf');

  return (
    <div className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-lg border border-gray-200 group">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isImage ? 'bg-blue-100' : isPdf ? 'bg-red-100' : 'bg-gray-200'
      }`}>
        {isImage ? (
          <Image className={`w-4 h-4 ${isImage ? 'text-blue-600' : 'text-gray-500'}`} />
        ) : (
          <FileText className={`w-4 h-4 ${isPdf ? 'text-red-600' : 'text-gray-500'}`} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-gray-800 truncate">{file?.name}</p>
        {file?.size && (
          <p className="text-[10px] text-gray-400">{(file?.size / 1024)?.toFixed(1)} Ko</p>
        )}
      </div>
      {isUploaded ? (
        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
      ) : (
        <button
          onClick={() => onRemove?.(file)}
          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all rounded"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

// ─── Existing Dispute Card ────────────────────────────────────────────────────

const ExistingDisputeCard = ({ dispute }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = DISPUTE_STATUS_CONFIG?.[dispute?.status] || DISPUTE_STATUS_CONFIG?.OPEN;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className={`w-9 h-9 rounded-xl ${cfg?.bg} flex items-center justify-center flex-shrink-0`}>
          {cfg?.icon && <cfg.icon className={`w-4 h-4 ${cfg?.color}`} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{dispute?.reason?.slice(0, 60) || 'Litige'}{dispute?.reason?.length > 60 ? '…' : ''}</p>
          <p className="text-xs text-gray-500 mt-0.5">{formatDateShort(dispute?.created_at)}</p>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border flex-shrink-0 ${cfg?.bg} ${cfg?.color} ${cfg?.border}`}>
          {cfg?.label}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 space-y-4 bg-gray-50/50">
          <ResolutionTimeline dispute={dispute} />

          {dispute?.evidence_urls?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Preuves soumises ({dispute?.evidence_urls?.length})</p>
              <div className="space-y-1.5">
                {dispute?.evidence_urls?.map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-xs text-blue-600 font-medium"
                  >
                    <Paperclip className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">Preuve {i + 1}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Dispute Submission Drawer ──────────────────────────────────────────

const DisputeSubmissionDrawer = ({ transaction, onClose, onDisputeSubmitted }) => {
  const [disputes, setDisputes] = useState([]);
  const [loadingDisputes, setLoadingDisputes] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchDisputes = useCallback(async () => {
    if (!transaction?.id) return;
    setLoadingDisputes(true);
    try {
      const { data } = await supabase
        ?.from('escrow_disputes')
        ?.select('*')
        ?.eq('transaction_id', transaction?.id)
        ?.order('created_at', { ascending: false });
      setDisputes(data || []);
    } catch (_) {}
    setLoadingDisputes(false);
  }, [transaction?.id]);

  useEffect(() => { fetchDisputes(); }, [fetchDisputes]);

  const hasOpenDispute = disputes?.some(d => ['OPEN', 'open', 'UNDER_REVIEW']?.includes(d?.status));
  const canSubmitNew = !hasOpenDispute && !['COMPLETED', 'RELEASED', 'REFUNDED', 'CANCELLED']?.includes(transaction?.status);

  const handleFileSelect = (e) => {
    const selected = Array.from(e?.target?.files || []);
    const allowed = selected?.filter(f => {
      const ok = f?.size <= 10 * 1024 * 1024; // 10MB max
      if (!ok) showToast(`${f?.name} dépasse 10 Mo`, 'error');
      return ok;
    });
    setFiles(prev => [...prev, ...allowed]?.slice(0, 5)); // max 5 files
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const removeFile = (file) => {
    setFiles(prev => prev?.filter(f => f !== file));
  };

  const uploadEvidence = async () => {
    if (!files?.length) return [];
    const urls = [];
    for (const file of files) {
      const ext = file?.name?.split('.')?.pop();
      const path = `disputes/${transaction?.id}/${Date.now()}_${Math.random()?.toString(36)?.slice(2)}.${ext}`;
      const { data, error } = await supabase?.storage
        ?.from('dispute-evidence')
        ?.upload(path, file, { upsert: false });
      if (!error && data?.path) {
        const { data: urlData } = supabase?.storage?.from('dispute-evidence')?.getPublicUrl(data?.path);
        if (urlData?.publicUrl) urls?.push(urlData?.publicUrl);
      }
    }
    return urls;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!description?.trim() || !category) {
      showToast('Veuillez remplir tous les champs obligatoires', 'error');
      return;
    }

    setSubmitting(true);
    try {
      setUploading(true);
      const evidenceUrls = await uploadEvidence();
      setUploading(false);

      const { data: { user } } = await supabase?.auth?.getUser();
      const fullReason = `[${DISPUTE_CATEGORIES?.find(c => c?.value === category)?.label || category}] ${description?.trim()}`;

      const { error } = await supabase?.from('escrow_disputes')?.insert({
        transaction_id: transaction?.id,
        raised_by: user?.id,
        reason: fullReason,
        evidence_urls: evidenceUrls,
        status: 'OPEN',
      });

      if (error) throw error;

      // Update transaction status to DISPUTED
      await supabase?.from('escrow_transactions')
        ?.update({ status: 'DISPUTED', updated_at: new Date()?.toISOString() })
        ?.eq('id', transaction?.id);

      showToast('Litige soumis avec succès. Notre équipe vous contactera sous 48h.');
      setDescription('');
      setCategory('');
      setFiles([]);
      setShowForm(false);
      await fetchDisputes();
      onDisputeSubmitted?.();
    } catch (err) {
      showToast('Erreur lors de la soumission: ' + (err?.message || 'Réessayez'), 'error');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  const displayTitle = transaction?.item_title || transaction?.title || transaction?.property?.title || 'Transaction';
  const displayAmount = transaction?.total_amount || transaction?.amount_total;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2.5 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium transition-all ${
          toast?.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          {toast?.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast?.msg}
        </div>
      )}

      <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Gestion des litiges</h3>
              <p className="text-xs text-gray-500 truncate max-w-[220px]">{displayTitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-5">
          {/* Transaction summary */}
          <div className="bg-gradient-to-br from-gray-50 to-slate-100 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 font-medium">Montant en séquestre</span>
              <span className="text-base font-bold text-gray-900">{formatAmount(displayAmount, transaction?.currency)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Référence</span>
              <span className="text-xs font-mono font-semibold text-gray-700">{transaction?.reference || transaction?.id?.slice(0, 12)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Statut transaction</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                ['DISPUTE', 'DISPUTED']?.includes(transaction?.status) ? 'bg-red-100 text-red-700' :
                ['COMPLETED', 'RELEASED']?.includes(transaction?.status) ? 'bg-green-100 text-green-700' :
                'bg-amber-100 text-amber-700'
              }`}>
                {transaction?.status}
              </span>
            </div>
          </div>

          {/* Existing disputes */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Litiges ({disputes?.length})
              </h4>
              {canSubmitNew && (
                <button
                  onClick={() => setShowForm(p => !p)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition-colors"
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {showForm ? 'Annuler' : 'Nouveau litige'}
                </button>
              )}
            </div>

            {loadingDisputes ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            ) : disputes?.length === 0 && !showForm ? (
              <div className="text-center py-8 space-y-2">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                  <Shield className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm font-semibold text-gray-600">Aucun litige en cours</p>
                <p className="text-xs text-gray-400">Votre transaction se déroule normalement.</p>
                {canSubmitNew && (
                  <button
                    onClick={() => setShowForm(true)}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-semibold hover:bg-red-100 transition-colors"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Signaler un problème
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {disputes?.map(d => (
                  <ExistingDisputeCard key={d?.id} dispute={d} />
                ))}
              </div>
            )}

            {hasOpenDispute && (
              <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Clock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 font-medium">
                  Un litige est déjà en cours d'examen. Vous ne pouvez pas en soumettre un nouveau tant que le précédent n'est pas résolu.
                </p>
              </div>
            )}
          </div>

          {/* Submission form */}
          {showForm && canSubmitNew && (
            <form onSubmit={handleSubmit} className="space-y-4 border-t border-gray-200 pt-5">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare className="w-4 h-4 text-red-500" />
                <h4 className="text-sm font-bold text-gray-900">Soumettre un nouveau litige</h4>
              </div>

              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Catégorie du litige <span className="text-red-500">*</span>
                </label>
                <select
                  value={category}
                  onChange={e => setCategory(e?.target?.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white"
                >
                  <option value="">Sélectionnez une catégorie...</option>
                  {DISPUTE_CATEGORIES?.map(c => (
                    <option key={c?.value} value={c?.value}>{c?.label}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Description du problème <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e?.target?.value)}
                  required
                  rows={4}
                  placeholder="Décrivez en détail le problème rencontré, les dates, les montants concernés et toute information pertinente..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                />
                <p className="text-[10px] text-gray-400 mt-1">{description?.length}/2000 caractères</p>
              </div>

              {/* Evidence upload */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Preuves (optionnel) — max 5 fichiers, 10 Mo chacun
                </label>
                <div
                  onClick={() => fileInputRef?.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-5 text-center cursor-pointer hover:border-red-400 hover:bg-red-50/30 transition-colors"
                >
                  <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-600">Cliquez pour ajouter des fichiers</p>
                  <p className="text-xs text-gray-400 mt-1">Images, PDF, documents — max 10 Mo par fichier</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {files?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {files?.map((file, i) => (
                      <EvidenceFileItem key={i} file={file} onRemove={removeFile} />
                    ))}
                  </div>
                )}
              </div>

              {/* Info notice */}
              <div className="flex items-start gap-2.5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Shield className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800">
                  Notre équipe examinera votre litige sous <strong>48 heures ouvrables</strong>. Les fonds resteront bloqués en séquestre jusqu'à résolution.
                </p>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setFiles([]); setDescription(''); setCategory(''); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting || !description?.trim() || !category}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      {uploading ? 'Envoi des preuves...' : 'Soumission...'}
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4" />
                      Soumettre le litige
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Closed transaction notice */}
          {['COMPLETED', 'RELEASED', 'REFUNDED', 'CANCELLED']?.includes(transaction?.status) && (
            <div className="flex items-start gap-2.5 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <CheckCircle className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-600">
                Cette transaction est terminée. Il n'est plus possible de soumettre un litige.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DisputeSubmissionDrawer;
